// Adaptive Music System Configuration
// Node-based system matching FlowVisualization
//
// Architecture:
// - Each flow node = a trackObject containing a looping timeline
// - All nodes sit on the main timeline at sequential positions
// - Each connection = a transition with unique targetIndex
//
// Flow Structure:
//                     [exploration] ────────┐
//                         ↗                 ↓
// [approach] → [branch] ────────────→ [resolution] → [output]
//                         ↘                 ↑
//                     [tension] → [combat] ─┘
//                                           │
//                         ←─────────────────┘ (loopback)

// Timeline positions for each node (8 bars each at 120bpm = 16 seconds)
const NODE_DURATION = 16; // seconds per node section
const LOOP_DURATION = 8; // internal loop length

export const musicSystem = {
  rootTimeline: "main_timeline",

  // Node positions on main timeline
  nodePositions: {
    approach: 0,
    branch: 16,
    exploration: 32,
    tension: 48,
    combat: 64,
    resolution: 80,
    output: 96,
  },

  timelines: {
    // Main timeline - contains all node sections
    main_timeline: {
      id: "main_timeline",
      length: 112, // 7 nodes × 16 seconds
      loopRegions: [], // No global loop - transitions control flow
      trackObjects: [
        "approach_obj",
        "branch_obj",
        "exploration_obj",
        "tension_obj",
        "combat_obj",
        "resolution_obj",
        "output_obj",
      ],
    },

    // Individual node timelines (each loops internally)
    approach_timeline: {
      id: "approach_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["approach_track_obj"],
    },
    branch_timeline: {
      id: "branch_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["branch_track_obj"],
    },
    exploration_timeline: {
      id: "exploration_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["exploration_track_obj"],
    },
    tension_timeline: {
      id: "tension_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["tension_track_obj"],
    },
    combat_timeline: {
      id: "combat_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["combat_track_obj"],
    },
    resolution_timeline: {
      id: "resolution_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["resolution_track_obj"],
    },
    output_timeline: {
      id: "output_timeline",
      length: LOOP_DURATION,
      loopRegions: [{ start: 0, end: LOOP_DURATION }],
      trackObjects: ["output_track_obj"],
    },
  },

  trackObjects: {
    // Main timeline node containers (type: timeline)
    approach_obj: {
      id: "approach_obj",
      type: "timeline",
      timelineRef: "approach_timeline",
      startTime: 0,
      endTime: 16,
      envelope: { attack: 0.5, release: 1.0 },
    },
    branch_obj: {
      id: "branch_obj",
      type: "timeline",
      timelineRef: "branch_timeline",
      startTime: 16,
      endTime: 32,
      envelope: { attack: 0.5, release: 1.0 },
    },
    exploration_obj: {
      id: "exploration_obj",
      type: "timeline",
      timelineRef: "exploration_timeline",
      startTime: 32,
      endTime: 48,
      envelope: { attack: 0.5, release: 1.0 },
    },
    tension_obj: {
      id: "tension_obj",
      type: "timeline",
      timelineRef: "tension_timeline",
      startTime: 48,
      endTime: 64,
      envelope: { attack: 0.5, release: 1.0 },
    },
    combat_obj: {
      id: "combat_obj",
      type: "timeline",
      timelineRef: "combat_timeline",
      startTime: 64,
      endTime: 80,
      envelope: { attack: 0.5, release: 1.0 },
    },
    resolution_obj: {
      id: "resolution_obj",
      type: "timeline",
      timelineRef: "resolution_timeline",
      startTime: 80,
      endTime: 96,
      envelope: { attack: 0.5, release: 1.0 },
    },
    output_obj: {
      id: "output_obj",
      type: "timeline",
      timelineRef: "output_timeline",
      startTime: 96,
      endTime: 112,
      envelope: { attack: 0.5, release: 1.0 },
    },

    // Nested timeline track references (type: track)
    approach_track_obj: {
      id: "approach_track_obj",
      type: "track",
      trackRef: "approach_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
    branch_track_obj: {
      id: "branch_track_obj",
      type: "track",
      trackRef: "branch_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
    exploration_track_obj: {
      id: "exploration_track_obj",
      type: "track",
      trackRef: "exploration_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
    tension_track_obj: {
      id: "tension_track_obj",
      type: "track",
      trackRef: "tension_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
    combat_track_obj: {
      id: "combat_track_obj",
      type: "track",
      trackRef: "combat_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
    resolution_track_obj: {
      id: "resolution_track_obj",
      type: "track",
      trackRef: "resolution_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
    output_track_obj: {
      id: "output_track_obj",
      type: "track",
      trackRef: "output_track",
      startTime: 0,
      endTime: LOOP_DURATION,
    },
  },

  // Audio tracks (placeholder URLs - replace with actual audio)
  tracks: {
    approach_track: {
      id: "approach_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
    branch_track: {
      id: "branch_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
    exploration_track: {
      id: "exploration_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
    tension_track: {
      id: "tension_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
    combat_track: {
      id: "combat_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
    resolution_track: {
      id: "resolution_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
    output_track: {
      id: "output_track",
      url: "/adaptiveMusic/amSectionA.mp3", // Placeholder
      playbackOffset: 0,
    },
  },

  // Transitions - one per connection in the flow
  // targetIndex maps to transition parameter (1-8)
  transitions: {
    // Connection 1: approach → branch
    trans_approach_branch: {
      id: "trans_approach_branch",
      from: "approach",
      to: "branch",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 4, // Trigger within approach section
      targetPosition: 16, // Jump to branch
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 1,
    },

    // Connection 2: branch → exploration
    trans_branch_exploration: {
      id: "trans_branch_exploration",
      from: "branch",
      to: "exploration",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 20, // Trigger within branch section
      targetPosition: 32, // Jump to exploration
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 2,
    },

    // Connection 3: branch → tension
    trans_branch_tension: {
      id: "trans_branch_tension",
      from: "branch",
      to: "tension",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 20, // Trigger within branch section
      targetPosition: 48, // Jump to tension
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 3,
    },

    // Connection 4: exploration → resolution
    trans_exploration_resolution: {
      id: "trans_exploration_resolution",
      from: "exploration",
      to: "resolution",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 36, // Trigger within exploration section
      targetPosition: 80, // Jump to resolution
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 4,
    },

    // Connection 5: tension → combat
    trans_tension_combat: {
      id: "trans_tension_combat",
      from: "tension",
      to: "combat",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 52, // Trigger within tension section
      targetPosition: 64, // Jump to combat
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 5,
    },

    // Connection 6: combat → resolution
    trans_combat_resolution: {
      id: "trans_combat_resolution",
      from: "combat",
      to: "resolution",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 68, // Trigger within combat section
      targetPosition: 80, // Jump to resolution
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 6,
    },

    // Connection 7: resolution → output
    trans_resolution_output: {
      id: "trans_resolution_output",
      from: "resolution",
      to: "output",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 84, // Trigger within resolution section
      targetPosition: 96, // Jump to output
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 7,
    },

    // Connection 8: resolution → branch (loopback)
    trans_resolution_branch: {
      id: "trans_resolution_branch",
      from: "resolution",
      to: "branch",
      url: "/adaptiveMusic/amTransition.mp3",
      triggerPosition: 84, // Trigger within resolution section
      targetPosition: 16, // Jump back to branch
      duration: 2,
      prevPosEnd: 1,
      nextPosStart: 1,
      targetIndex: 8,
    },
  },

  initialPosition: 0,
  bpm: 120,
  bars: 4,
};

// Helper: Get current node from timeline position
export const getNodeFromPosition = (position) => {
  const { nodePositions } = musicSystem;
  const nodes = Object.entries(nodePositions).sort((a, b) => b[1] - a[1]);

  for (const [nodeName, startPos] of nodes) {
    if (position >= startPos) {
      return nodeName;
    }
  }
  return "approach";
};

// Helper: Get available transitions from a node
export const getTransitionsFromNode = (nodeName) => {
  return Object.values(musicSystem.transitions).filter(
    (t) => t.from === nodeName,
  );
};

// Helper: Get transition by from/to
export const getTransition = (from, to) => {
  return Object.values(musicSystem.transitions).find(
    (t) => t.from === from && t.to === to,
  );
};
